import { flatten } from "ramda";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DialogComponent, Modal } from "widgets";
import { useOverlay } from "widgets/hooks";

import { pluralizeWord } from "@/app-utils/string.ts";
import type { Attribute, InputTableQuerySpec, ProjectFile } from "@/graphql.ts";
import type { IEditable } from "@/redux/modules/data-editing.ts";
import {
  clearSampleEditingData,
  loadSampleDataFromAnnotationColumns,
  saveSamples,
  type TSampleCellValue,
  uniquenessValidations,
} from "@/redux/modules/sample-editing.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE } from "@/settings/settings.js";
import { useCountInvalidCells } from "@/ui-modules/playbooks/phenotype/EditingGrid.tsx";
import SampleEditingGrid from "@/ui-modules/playbooks/sample/SampleEditingGrid.tsx";

interface SampleFileEditorDialogProps {
  isOpen?: boolean;
  onClose: () => void;
  fileAttributeId?: number;
  sampleIds: number[];
  sampleTypeId: number;
}

const SampleFileEditorControlledDialogRender: FC<SampleFileEditorDialogProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const attributes = useSelector<IAppState, Attribute[]>((state) => state.sampleEditingState.sampleAttributes);
  const editingGrid = useSelector<IAppState, IEditable[]>((state) => state.sampleEditingState.editingGrid);

  const invalidCellsCount: number = useCountInvalidCells(editingGrid);
  const [formErrorMessage, setFormErrorMessage] = useState<string>();
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState<string>();
  const [validating, setValidating] = useState<boolean>(false);

  const sampleEditingTableQuerySpec: InputTableQuerySpec = useMemo(() => {
    const tableQuerySpec: InputTableQuerySpec = {
      id: props.sampleTypeId,
      idType: DATATABLE_ID_TYPE.ENTITY,
      queryType: ENTITY_TYPE.SAMPLE_TYPE,
      resultType: ENTITY_TYPE.SAMPLE,
      restrictions: {
        selection: {
          selectedResultIds: props.sampleIds,
        },
      },
    };
    return tableQuerySpec;
  }, [props.sampleIds]);

  useEffect(() => {
    void dispatch(
      loadSampleDataFromAnnotationColumns(
        props.sampleTypeId,
        sampleEditingTableQuerySpec,
        props.fileAttributeId ? [props.fileAttributeId] : [],
      ),
    ).catch((error: Error) => {
      setFormErrorMessage(error.message);
    });
    return () => {
      dispatch(clearSampleEditingData());
    };
  }, []);

  const onSave = useCallback(() => {
    setSubmissionErrorMessage(undefined);
    setValidating(true);

    dispatch(uniquenessValidations())
      .then(() => setValidating(false))
      .then(() => dispatch(saveSamples(sampleEditingTableQuerySpec)))
      .then(props.onClose)
      .catch((error: Error) => {
        setSubmissionErrorMessage(error.message);
        setValidating(false);
      });
  }, [sampleEditingTableQuerySpec]);

  useEffect(() => {
    if (invalidCellsCount > 0) {
      setFormErrorMessage(
        `${invalidCellsCount} ${pluralizeWord("cell", invalidCellsCount)} ${pluralizeWord(
          "is",
          invalidCellsCount,
        )} invalid.`,
      );
      return;
    }

    if (props.fileAttributeId) {
      const ngsDataFilesValidationError = validateSampleFiles(editingGrid, props.fileAttributeId);
      if (ngsDataFilesValidationError) {
        setFormErrorMessage(ngsDataFilesValidationError);
        return;
      }
    }

    setFormErrorMessage(undefined);
  }, [editingGrid, invalidCellsCount, props.fileAttributeId]);

  return (
    <DialogComponent
      title="Edit Samples"
      width={580}
      closeFn={props.onClose}
      mainButton={{
        label: "Save",
        onPress: onSave,
        isDisabled: !!formErrorMessage,
        isLoading: validating,
      }}
      errorCallout={{ message: formErrorMessage ?? submissionErrorMessage }}
      fullscreen={true}
      contentStyle={{ padding: 0 }}
    >
      <div className="grid-modal-border" style={{ height: "100%" }}>
        <SampleEditingGrid
          attributes={attributes}
          templatesLookup={undefined} // the templates lookup does not apply there, since the read-only situations happens on row level
        />
      </div>
    </DialogComponent>
  );
};

export const SampleFileEditorControlledDialog: FC<SampleFileEditorDialogProps> = (props) => {
  const { overlayProps, state } = useOverlay({
    isOpen: props.isOpen,
    onOpenChange(isOpen) {
      if (!isOpen) props.onClose();
    },
  });

  return (
    <Modal state={state} {...overlayProps}>
      {/* Need to mount / unmount for resetting */}
      {props.isOpen && <SampleFileEditorControlledDialogRender {...props} />}
    </Modal>
  );
};

// this function is similar to validateNgsDataFiles, but not meant for Classic Processor and using a different configuration pattern
function validateSampleFiles(editingGrid: IEditable[], fileAttributeId: number) {
  const cellValues: TSampleCellValue[] = editingGrid.map((row) => row[fileAttributeId]);

  // gather the list of ngs files for additional validation
  const files: ProjectFile[] = flatten(cellValues.map((cellValue: TSampleCellValue) => cellValue as ProjectFile[]));

  // make sure the files are all accessible (might be attached by someone else or historically)
  const restrictedFiles = files.filter((file) => file?.hidden || file?.deleted);
  if (restrictedFiles.length > 0) {
    return "Some of the files that you provide to the analysis have a restricted access.";
  }

  return;
}
